import React from 'react';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function AdminMessagesTab({ conversations, selectedConversation, messages, newMessage, sendingMessage, userId, onSelectConversation, onMessageChange, onSendMessage }) {
  return (
    <>
      <h2 className='text-2xl font-bold text-[#1A3C34] mb-6' style={{ fontFamily: 'Clash Display' }}>Message Management</h2>
      <div className='grid md:grid-cols-3 gap-6'>
        <Card className='bg-white rounded-2xl p-6'>
          <h3 className='text-lg font-bold text-[#1A3C34] mb-4'>Conversations</h3>
          <div className='space-y-2 max-h-96 overflow-y-auto'>
            {conversations.map((conversation, index) => (
              <div 
                key={conversation.user_id} 
                className={`p-3 rounded-lg cursor-pointer transition-colors ${
                  selectedConversation?.user_id === conversation.user_id 
                    ? 'bg-[#D4F246] text-[#1A3C34]' 
                    : 'bg-gray-50 hover:bg-gray-100'
                }`}
                onClick={() => onSelectConversation(conversation)}
                data-testid={`conversation-${index}`}
              >
                <div className='font-medium'>{conversation.user_name}</div>
                <div className='text-sm text-gray-600'>{conversation.user_email}</div>
                <div className='text-xs text-gray-500 mt-1'>
                  {conversation.unread_count > 0 && (
                    <span className='bg-red-500 text-white px-2 py-1 rounded-full text-xs'>
                      {conversation.unread_count} new
                    </span>
                  )}
                </div>
              </div>
            ))}
            {conversations.length === 0 && (
              <div className='text-gray-500 text-center py-8'>No conversations yet</div>
            )}
          </div>
        </Card>

        <Card className='bg-white rounded-2xl p-6 md:col-span-2'>
          {selectedConversation ? (
            <>
              <div className='flex justify-between items-center mb-4 pb-4 border-b'>
                <div>
                  <h3 className='text-lg font-bold text-[#1A3C34]'>{selectedConversation.user_name}</h3>
                  <p className='text-sm text-gray-600'>{selectedConversation.user_email}</p>
                </div>
              </div>
              
              <div className='space-y-4 max-h-80 overflow-y-auto mb-4' data-testid='messages-list'>
                {messages.map((message, index) => (
                  <div 
                    key={message.id} 
                    className={`flex ${message.sender_id === userId ? 'justify-end' : 'justify-start'}`}
                    data-testid={`message-${index}`}
                  >
                    <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                      message.sender_id === userId 
                        ? 'bg-[#1A3C34] text-white' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {message.content && <p className='text-sm mb-2'>{message.content}</p>}
                      
                      {message.file_url && (
                        <div className='mt-2'>
                          {message.file_type === 'image' && (
                            <img
                              src={`${API}/messages/media/${message.file_url}`}
                              alt={message.file_name || 'Image'}
                              className='max-w-full rounded max-h-48 object-cover cursor-pointer'
                              onClick={() => window.open(`${API}/messages/media/${message.file_url}`, '_blank')}
                            />
                          )}
                          {message.file_type === 'video' && (
                            <video controls className='max-w-full rounded max-h-48' src={`${API}/messages/media/${message.file_url}`}>
                              Your browser does not support video playback.
                            </video>
                          )}
                          {message.file_type === 'document' && (
                            <a
                              href={`${API}/messages/media/${message.file_url}`}
                              target='_blank'
                              rel='noopener noreferrer'
                              className='flex items-center gap-2 text-sm underline'
                            >
                              {message.file_name || 'Download file'}
                            </a>
                          )}
                        </div>
                      )}
                      
                      <p className='text-xs mt-1 opacity-70'>
                        {new Date(message.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
                {messages.length === 0 && (
                  <div className='text-gray-500 text-center py-8'>No messages in this conversation</div>
                )}
              </div>

              <form onSubmit={onSendMessage} className='flex gap-2'>
                <Input
                  value={newMessage}
                  onChange={(e) => onMessageChange(e.target.value)}
                  placeholder='Type your message...'
                  className='flex-1'
                  disabled={sendingMessage}
                  data-testid='message-input'
                />
                <Button 
                  type='submit' 
                  disabled={sendingMessage || !newMessage.trim()}
                  className='bg-[#1A3C34] text-white rounded-full'
                  data-testid='send-message-btn'
                >
                  {sendingMessage ? 'Sending...' : 'Send'}
                </Button>
              </form>
            </>
          ) : (
            <div className='text-gray-500 text-center py-16'>
              Select a conversation to view messages
            </div>
          )}
        </Card>
      </div>
    </>
  );
}
